import { parseCoordGeomDetailed, parseStationEquations } from './coordGeom';
import {
  childPath,
  childrenNamed,
  descendants,
  firstChild,
  firstDescendant,
  numAttr,
  parentName,
  parsePoint,
} from './dom';
import { LandxmlNonMetreUnitsError, TIN_INDEX_ATTR, type RawTin } from './tin';
import type { AlignmentDegradation, KeyPoint, P3, StationEquation } from './types';
import { assertMetreUnits, declaredNonMetreUnit } from './units';

/**
 * Plain-data LandXML readers: no `three`, no DOM implementation, only the
 * `dom.ts` helpers, so the browser's 3D parsers (`app/src/core/landxml.ts`)
 * and the MAP_2D Lambda (`@xmldom/xmldom`) read a file identically. The 3D
 * side wraps this data in THREE objects; it must not re-read the DOM itself.
 *
 * Unit guards keep the 3D parsers' long-standing behaviour: each reader
 * resolves the declared unit once (and only when it has a container to read,
 * so a file without one is not warned about twice) and throws
 * `LandxmlNonMetreUnitsError` only where it would actually have returned
 * something. `readSurfaceTins` refuses eagerly — it is the first reader the
 * layer calls, so it is where the single user-facing refusal comes from.
 */

export interface BreaklineData {
  surfaceName: string;
  name: string;
  points: P3[];
}

export interface ParcelData {
  name: string;
  /** Closed boundary (first === last). */
  ring: P3[];
}

export interface StructData {
  name: string;
  /** `[x, y, elevSump]`. */
  center: P3;
  innerDiameter: number;
  outerDiameter: number;
  height: number;
  rimElev: number;
  sumpElev: number;
  /** Invert elevation keyed by the pipe name it refers to. */
  invertElevs: Map<string, number>;
}

export type PipeShape = 'circular' | 'egg' | 'elliptical' | 'rectangular';

export interface PipeData {
  name: string;
  refStart: string;
  refEnd: string;
  /** Metres (CircPipe diameters are written in millimetres and converted here). */
  diameter: number;
  shape: PipeShape;
}

export interface PipeNetworkData {
  name: string;
  structs: StructData[];
  pipes: PipeData[];
}

export interface AlignmentData {
  /** The `<Alignment>` element, for readers that need more (profiles). */
  element: Element;
  name: string;
  points: P3[];
  keyPoints: KeyPoint[];
  degradations: AlignmentDegradation[];
  /** Analytic (design) 2D length — see `parseCoordGeomDetailed`. */
  length: number;
  staStart: number;
  equations: StationEquation[];
  declaredLength?: number;
}

export interface SurfaceTin {
  name: string;
  /** Unique vertices, `[easting, northing, z]` triples, absolute coordinates. */
  positions: Float64Array;
  /** Triangle corners, 3 per triangle, indexing into `positions`. */
  index: Uint32Array;
  /** True when served from an `extractTins` mesh (`positions` is its `positions64`). */
  fromExtractedTin: boolean;
}

const grandParentName = (el: Element): string | null => {
  const p = el.parentNode;
  return p && p.nodeType === 1 ? parentName(p as Element) : null;
};

export function readBreaklines(doc: Document): BreaklineData[] {
  const out: BreaklineData[] = [];
  // `SourceData > Breaklines > Breakline` — resolve/warn units only when one exists.
  const hasBreaklines = descendants(doc, 'Breakline').some(
    (b) => parentName(b) === 'Breaklines' && grandParentName(b) === 'SourceData',
  );
  const nonMetreUnit = hasBreaklines ? declaredNonMetreUnit(doc) : null;
  for (const surface of descendants(doc, 'Surface')) {
    const surfaceName = surface.getAttribute('name') || 'Surface';
    const sourceData = firstDescendant(surface, 'SourceData');
    if (!sourceData) continue;
    const container = firstDescendant(sourceData, 'Breaklines');
    if (!container) continue;
    for (const breakline of descendants(container, 'Breakline')) {
      const name = breakline.getAttribute('name') || 'Breakline';
      const pnt = firstDescendant(breakline, 'PntList3D');
      if (!pnt?.textContent) continue;
      // Refused, never scaled. Fires only when there is a real breakline to refuse.
      if (nonMetreUnit !== null) throw new LandxmlNonMetreUnitsError(nonMetreUnit);
      const coords = pnt.textContent.trim().split(/\s+/).map(Number);
      const points: P3[] = [];
      // northing easting elevation → [easting, northing, z]
      for (let i = 0; i < coords.length; i += 3) points.push([coords[i + 1], coords[i], coords[i + 2] ?? 0]);
      if (points.length >= 2) out.push({ surfaceName, name, points });
    }
  }
  return out;
}

export function readParcels(doc: Document): ParcelData[] {
  const out: ParcelData[] = [];
  const parcels = childPath(doc, 'Parcels', 'Parcel');
  const nonMetreUnit = parcels.length > 0 ? declaredNonMetreUnit(doc) : null;
  for (const parcel of parcels) {
    const name = parcel.getAttribute('name') || 'Parcel';
    const coordGeom = firstDescendant(parcel, 'CoordGeom');
    if (!coordGeom) continue;
    if (nonMetreUnit !== null) throw new LandxmlNonMetreUnitsError(nonMetreUnit);
    const ring = parseCoordGeomDetailed(coordGeom).points;
    if (ring.length < 2) continue;
    const a = ring[0];
    const b = ring[ring.length - 1];
    if (ring.length > 2 && (a[0] !== b[0] || a[1] !== b[1] || a[2] !== b[2])) ring.push(a);
    out.push({ name, ring });
  }
  return out;
}

function readPipeShape(pipeEl: Element): { diameter: number; shape: PipeShape } {
  const circ = firstDescendant(pipeEl, 'CircPipe');
  if (circ) return { diameter: parseFloat(circ.getAttribute('diameter') || '3000') / 1000, shape: 'circular' };
  const egg = firstDescendant(pipeEl, 'EggPipe');
  if (egg) {
    const h = parseFloat(egg.getAttribute('height') || '0.3');
    const w = parseFloat(egg.getAttribute('width') || String(h * 0.67));
    return { diameter: Math.max(w, h), shape: 'egg' };
  }
  const elli = firstDescendant(pipeEl, 'ElliPipe');
  if (elli) {
    const h = parseFloat(elli.getAttribute('height') || '0.3');
    const w = parseFloat(elli.getAttribute('width') || '0.3');
    return { diameter: Math.max(w, h), shape: 'elliptical' };
  }
  const rect = firstDescendant(pipeEl, 'RectPipe');
  if (rect) {
    const h = parseFloat(rect.getAttribute('height') || '0.3');
    const w = parseFloat(rect.getAttribute('width') || '0.3');
    return { diameter: Math.sqrt(w * w + h * h), shape: 'rectangular' };
  }
  return { diameter: 0.3, shape: 'circular' };
}

export function readPipeNetworks(doc: Document): PipeNetworkData[] {
  const out: PipeNetworkData[] = [];
  const networks = childPath(doc, 'PipeNetworks', 'PipeNetwork');
  const nonMetreUnit = networks.length > 0 ? declaredNonMetreUnit(doc) : null;
  for (const network of networks) {
    const name = network.getAttribute('name') || 'PipeNetwork';
    const structEls = childPath(network, 'Structs', 'Struct');
    const pipeEls = childPath(network, 'Pipes', 'Pipe');
    // Fires only when this network actually declares a struct or pipe.
    if (nonMetreUnit !== null && (structEls.length > 0 || pipeEls.length > 0)) {
      throw new LandxmlNonMetreUnitsError(nonMetreUnit);
    }
    const structs: StructData[] = [];
    for (const structEl of structEls) {
      const structName = structEl.getAttribute('name') || '';
      const center = firstDescendant(structEl, 'Center');
      if (!center?.textContent) continue;
      const [x, y, z] = parsePoint(center.textContent);
      const invertElevs = new Map<string, number>();
      for (const inv of descendants(structEl, 'InvertElev')) {
        const elev = parseFloat(inv.getAttribute('elev') || '0');
        const refPipe = inv.getAttribute('refPipe') || '';
        if (refPipe) invertElevs.set(refPipe, elev);
      }
      const circ = firstDescendant(structEl, 'CircStruct');
      const rect = firstDescendant(structEl, 'RectStruct');
      let innerDiameter = 1.0;
      let outerDiameter = 1.2;
      if (circ) {
        innerDiameter = parseFloat(circ.getAttribute('diameter') || '1000.0') / 1000;
        outerDiameter = innerDiameter + 0.2;
      } else if (rect) {
        const w = parseFloat(rect.getAttribute('width') || '1.0');
        const l = parseFloat(rect.getAttribute('length') || '1.0');
        innerDiameter = Math.max(w, l);
        outerDiameter = innerDiameter + 0.2;
      }
      const sumpElev = parseFloat(structEl.getAttribute('elevSump') || String(z));
      const rimElev = parseFloat(structEl.getAttribute('elevRim') || String(z + 2));
      let height = Math.abs(rimElev - sumpElev);
      if (height < 0.1) height = 2.0;
      structs.push({
        name: structName,
        center: [x, y, sumpElev],
        innerDiameter,
        outerDiameter,
        height,
        rimElev,
        sumpElev,
        invertElevs,
      });
    }
    const pipes: PipeData[] = pipeEls.map((pipeEl) => ({
      name: pipeEl.getAttribute('name') || 'Pipe',
      refStart: pipeEl.getAttribute('refStart') || '',
      refEnd: pipeEl.getAttribute('refEnd') || '',
      ...readPipeShape(pipeEl),
    }));
    out.push({ name, structs, pipes });
  }
  return out;
}

const warnToConsole = (name: string, w: string) => console.warn(`LandXML alignment "${name}": ${w}`);

export function readAlignments(
  doc: Document,
  onWarning: (name: string, warning: string) => void = warnToConsole,
): AlignmentData[] {
  const out: AlignmentData[] = [];
  const containers = descendants(doc, 'Alignments');
  const nonMetreUnit = containers.length > 0 ? declaredNonMetreUnit(doc) : null;
  for (const container of containers) {
    for (const alignment of childrenNamed(container, 'Alignment')) {
      const name = alignment.getAttribute('name') || 'Alignment';
      const coordGeom = firstChild(alignment, 'CoordGeom');
      if (!coordGeom) continue;
      // Radii, stations and profile values are all in the file's own unit —
      // refused, never scaled (corridor-trust T5).
      if (nonMetreUnit !== null) throw new LandxmlNonMetreUnitsError(nonMetreUnit);
      const { points, keyPoints, warnings, degradations, length } = parseCoordGeomDetailed(coordGeom);
      for (const w of warnings) onWarning(name, w);
      if (points.length < 2) continue;
      const staStart = numAttr(alignment, 'staStart') ?? 0;
      out.push({
        element: alignment,
        name,
        points,
        keyPoints,
        degradations,
        length,
        staStart,
        equations: parseStationEquations(alignment, staStart),
        declaredLength: numAttr(alignment, 'length') ?? undefined,
      });
    }
  }
  return out;
}

/**
 * Every TIN surface as an indexed mesh: the `extractTins` result where the
 * light document points at one, else read from the DOM. Hidden faces (`i="1"`)
 * are skipped on both paths. Indexed by point id so shared vertices stay
 * shared (the outline needs edge identity, not coordinate equality).
 *
 * DOM path: one vertex per distinct `<P id>`, in first-seen order; a repeated
 * id overwrites its vertex in place (last wins), exactly the id → point map
 * the 3D parser always built — so its `vertexCount` is `positions.length / 3`.
 */
export function readSurfaceTins(doc: Document, tins: RawTin[] = []): SurfaceTin[] {
  const out: SurfaceTin[] = [];
  assertMetreUnits(doc);
  for (const surface of descendants(doc, 'Surface')) {
    const name = surface.getAttribute('name') || 'Surface';
    const definition = firstDescendant(surface, 'Definition');
    if (!definition) continue;
    const pntsEl = firstDescendant(definition, 'Pnts');
    const facesEl = firstDescendant(definition, 'Faces');
    if (!pntsEl || !facesEl) continue;
    const tinIndex = Number(pntsEl.getAttribute(TIN_INDEX_ATTR));
    if (pntsEl.hasAttribute(TIN_INDEX_ATTR) && tins[tinIndex]) {
      const t = tins[tinIndex];
      if (t.triangleCount > 0) out.push({ name, positions: t.positions64, index: t.index, fromExtractedTin: true });
      continue;
    }
    const vertexOf = new Map<number, number>();
    const xyz: number[] = [];
    for (const p of descendants(pntsEl, 'P')) {
      if (!p.textContent) continue;
      const id = parseInt(p.getAttribute('id') || '0');
      const [x, y, z] = parsePoint(p.textContent);
      let v = vertexOf.get(id);
      if (v === undefined) {
        v = xyz.length / 3;
        vertexOf.set(id, v);
      }
      xyz[v * 3] = x;
      xyz[v * 3 + 1] = y;
      xyz[v * 3 + 2] = z;
    }
    const index: number[] = [];
    for (const f of descendants(facesEl, 'F')) {
      // `i="1"` is LandXML's invisible face — a triangle the producer trimmed
      // away (Civil 3D flags everything outside the boundary like this).
      // Kept identical to `extractTins` and `surface_io.read_landxml_as_triangulation`.
      if (!f.textContent || f.getAttribute('i') === '1') continue;
      const ids = f.textContent.trim().split(/\s+/).map(Number);
      if (ids.length < 3) continue;
      const a = vertexOf.get(ids[0]);
      const b = vertexOf.get(ids[1]);
      const c = vertexOf.get(ids[2]);
      if (a === undefined || b === undefined || c === undefined) continue;
      index.push(a, b, c);
    }
    if (index.length === 0) continue;
    out.push({ name, positions: Float64Array.from(xyz), index: Uint32Array.from(index), fromExtractedTin: false });
  }
  return out;
}
